import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Spinner } from '../../components/common/Spinner';
import { Pill } from '../../components/ui/Pill';
import { MonthYearPicker } from '../../components/ui/MonthYearPicker';
import { formatCurrency, formatDisplayDate } from '../../utils/format';
import { ReportingMoney } from '../../components/common/ReportingMoney';
import { DATE_PLACEHOLDER, formatMonthYear } from '../../utils/dateFormat';
import { useToBeInvoicedQueue } from '../../hooks/useToBeInvoiced';
import type { AttentionReasonCode, TBIProjectRow } from '../../services/api/toBeInvoiced';

// Status badge color map — inline styles per UI-SPEC Color section.
// No Tailwind color classes used for semantic colors.
//
// MKS-207 — `blocked` joined the row status: a project whose uninvoiced hours
// include somebody with no rate would be billed at zero. It is the whole
// reason rate coverage moved into the queue, so it is shown before anybody
// opens the project.
//
// MKS-284 — `needs_attention` is the red half of the two-status queue; it
// reuses `blocked`'s red so the colour keeps meaning the same thing.
const STATUS_COLORS = {
  blocked: { bg: '#FEE2E2', fg: '#991B1B', dot: '#EF4444' },
  needs_attention: { bg: '#FEE2E2', fg: '#991B1B', dot: '#EF4444' },
  needs_approval: { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
  ready: { bg: '#D1FAE5', fg: '#065F46', dot: '#10B981' },
} as const;

const STATUS_LABELS: Record<TBIProjectRow['status'], string> = {
  blocked: 'Blocked — no rate',
  needs_approval: 'Needs approval',
  needs_attention: 'Needs attention',
  ready: 'Ready',
};

// MKS-284 — the four things that can hold an invoice up. Short on purpose: the
// detail lives on the project card and in the review panel, the queue only has
// to say which kind of fix the month still needs.
const REASON_LABELS: Record<AttentionReasonCode, string> = {
  no_rate: 'No rate',
  not_approved: 'Not approved',
  not_submitted: 'Not submitted',
  nothing_logged: 'Nothing logged',
};

/** Up to three names, then "+N" — a project with twelve people must not own the row. */
const MAX_REASON_NAMES = 3;

export function formatReasonNames(users: { userName: string }[]): string {
  if (users.length === 0) return '';
  const shown = users.slice(0, MAX_REASON_NAMES).map((u) => u.userName);
  const rest = users.length - shown.length;
  return rest > 0 ? `${shown.join(', ')} +${rest}` : shown.join(', ');
}

// MKS-207 — the contract-validity filter. `unset` is the common case (most
// non-SLA projects never record contract dates) and must never be filtered
// out by the "valid contract" view, or the queue would hide most of itself.
type ContractFilter = 'all' | 'covered' | 'problem';

const CONTRACT_LABELS: Record<NonNullable<TBIProjectRow['contractValidity']>, string> = {
  valid: 'Contract valid',
  unset: 'No contract dates',
  expired: 'Contract expired',
  not_started: 'Contract not started',
};

export function matchesContractFilter(row: TBIProjectRow, filter: ContractFilter): boolean {
  if (filter === 'all') return true;
  const validity = row.contractValidity ?? 'unset';
  const isProblem = validity === 'expired' || validity === 'not_started';
  return filter === 'problem' ? isProblem : !isProblem;
}

// MKS-205 — "1. 1. 2026 – 31. 12. 2026", with an en dash and an open end when
// either side is null. Returns null when the project carries no contract dates
// at all, so the cell can fall back to the em-dash placeholder.
function formatContractRange(row: TBIProjectRow): string | null {
  if (!row.contractStart && !row.contractEnd) return null;
  const from = row.contractStart ? formatDisplayDate(row.contractStart) : '…';
  const to = row.contractEnd ? formatDisplayDate(row.contractEnd) : '…';
  return `${from} – ${to}`;
}

function formatPeriodLabel(ym: string): string {
  // YYYY-MM → "October 2026" for empty-state copy (MKS-351: shared util).
  const label = formatMonthYear(ym);
  return label === DATE_PLACEHOLDER ? ym : label;
}

// Column widths (percent of the fixed-layout table). Sums to 100 so the table
// never overflows its card.
const COLUMN_WIDTHS = {
  project: '20%',
  contract: '15%',
  package: '11%',
  overage: '14%',
  external: '11%',
  openTimesheets: '12%',
  status: '17%',
} as const;

export default function InvoicePendingPage() {
  const navigate = useNavigate();

  // Period state defaults to the PREVIOUS month (YYYY-MM): invoicing always
  // bills the month that just closed, and the current month's timesheets are
  // still open (which painted every row red with "Not submitted").
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  });

  // MKS-207 — the contract-validity filter, "all" by default so the queue opens
  // on everything that is waiting to be invoiced.
  const [contractFilter, setContractFilter] = useState<ContractFilter>('all');
  const widths = COLUMN_WIDTHS;

  const { data, isLoading, error } = useToBeInvoicedQueue(period);
  const allRows = data?.data ?? [];
  const rows = allRows.filter((row: TBIProjectRow) =>
    matchesContractFilter(row, contractFilter),
  );

  // Loading state — centered Spinner per InvoiceListPage pattern.
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  // Error state — matches InvoiceListPage error path exactly.
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">
          Failed to load pending billing items. Refresh the page to try again.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page heading — matches InvoiceListPage chrome */}
      <h1 className="text-2xl font-semibold text-gray-900">To Be Invoiced</h1>

      {/* Period selector — shared MonthYearPicker (English-only, YYYY-MM contract) */}
      <div className="flex flex-wrap items-end gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Period</label>
          <MonthYearPicker value={period} onChange={setPeriod} label="Period" />
        </div>
        {/* MKS-207 — contract-validity filter */}
        <div>
          <span className="block text-sm font-semibold text-gray-700 mb-1">Contract</span>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs">
            {(
              [
                { v: 'all', label: 'All' },
                { v: 'covered', label: 'Covers the period' },
                { v: 'problem', label: 'Expired / not started' },
              ] as { v: ContractFilter; label: string }[]
            ).map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setContractFilter(opt.v)}
                className={`px-3 py-1.5 ${
                  contractFilter === opt.v
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Empty state — dashed-border per UI-SPEC / InvoiceListPage idiom */}
      {rows.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-200 p-12 text-center">
          <p className="text-gray-500">Nothing to review.</p>
          <p className="text-sm text-gray-400 mt-1">
            All billable projects are up to date for {formatPeriodLabel(period)}. Adjust the period
            or check back next month.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table
              className="divide-y divide-gray-200"
              style={{ width: '100%', tableLayout: 'fixed' }}
            >
              {/* Table header — bg-gray-50 pattern, text-xs font-semibold uppercase */}
              <thead className="bg-gray-50">
                <tr>
                  <th
                    style={{ width: widths.project }}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Project
                  </th>
                  {/* MKS-205 — contract window straight from the project record. */}
                  <th
                    style={{ width: widths.contract }}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Contract
                  </th>
                  <th
                    style={{ width: widths.package }}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Package
                  </th>
                  <th
                    style={{ width: widths.overage }}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Overage
                  </th>
                  <th
                    style={{ width: widths.external }}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    External costs
                  </th>
                  {/* MKS-207 — who is still holding the period up */}
                  <th
                    style={{ width: widths.openTimesheets }}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Open timesheets
                  </th>
                  <th
                    style={{ width: widths.status }}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                </tr>
              </thead>

              {/* Table body */}
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((row: TBIProjectRow) => {
                  const statusColors = STATUS_COLORS[row.status];
                  // MKS-207 — optional on the wire so an older backend's
                  // response still renders; default to "no signal".
                  const openTimesheets = row.openTimesheets ?? [];
                  const blockedUsers = row.blockedUsers ?? [];
                  const contractValidity = row.contractValidity ?? 'unset';
                  const overageColors =
                    row.overageStatus !== 'none'
                      ? STATUS_COLORS[row.overageStatus as 'needs_approval' | 'ready']
                      : null;
                  // MKS-205 — hidden at 0.
                  const unapprovedHours = row.unapprovedHours ?? 0;
                  const contractRange = formatContractRange(row);
                  // MKS-284 — an older backend still sends the three old
                  // statuses, so the pill only switches on a row that actually
                  // came back in the two-status shape.
                  const twoStatusRow =
                    row.status === 'needs_attention' || row.status === 'ready';
                  const reasons = row.reasons ?? [];

                  return (
                    <tr
                      key={row.projectId}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/projects/${row.projectId}`)}
                    >
                      {/* Project column — Link stops row-click propagation */}
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="truncate"
                          title={row.projectName}
                        >
                          <Link
                            to={`/projects/${row.projectId}`}
                            className="text-primary-600 hover:underline"
                          >
                            {row.projectName}
                          </Link>
                        </div>
                        {/* MKS-205 — hours booked in the period that approval
                            has not cleared yet, so the amounts leave them out. */}
                        {unapprovedHours > 0 && (
                          <p className="mt-1 text-xs font-normal" style={{ color: '#92400E' }}>
                            {unapprovedHours} h awaiting approval
                          </p>
                        )}
                        {/* MKS-136 — the billing periods this project is waiting
                            to be invoiced for, with the name and amount the
                            operator bills. A recurring period becomes ready at
                            its end date; a phase period when the PM marks it
                            done. Absent while the release flag is off. */}
                        {/* MKS-280 — hours an earlier month's invoice left
                            behind. That month's own row is gone (a period
                            shows once, on the invoice that closed it), so
                            naming the month here is the only way the queue
                            and the project card's "Logged but not invoiced"
                            can be reconciled. */}
                        {(row.carriedOverHours ?? 0) > 0 && (
                          <p
                            className="mt-1 text-xs font-normal"
                            style={{ color: '#92400E' }}
                            data-testid="queue-carried-over"
                          >
                            {row.carriedOverHours} h carried over from{' '}
                            {(row.carriedOverPeriods ?? []).join(', ')}
                          </p>
                        )}
                        {row.billingPeriods?.map((period) => (
                          <p
                            key={period.id}
                            className="mt-1 text-xs font-normal text-gray-600"
                            data-testid={`queue-billing-period-${period.id}`}
                          >
                            {period.name}
                            {period.plannedAmount !== null && (
                              <>
                                {' · '}
                                {formatCurrency(
                                  period.plannedAmount,
                                  false,
                                  period.currency ?? 'CZK',
                                )}
                              </>
                            )}
                          </p>
                        ))}
                      </td>

                      {/* MKS-205 — contract validity window ("—" when open-ended) */}
                      <td className="px-6 py-4 text-sm text-gray-900 tabular-nums">
                        {contractRange ?? <span className="text-gray-400">—</span>}
                      </td>

                      {/* Package column — CZK amount or "—" */}
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {row.packageAmount != null ? (
                          /* MKS-314 follow-up — a CZK ledger aggregate for ONE
                             billing period, so it converts at that period's
                             own month rate. The planned amounts above are
                             NOT converted: they already carry their contract
                             currency, and relabelling a denominated figure is
                             what RESEARCH Pitfall 3 forbids. */
                          <ReportingMoney value={row.packageAmount} month={period} />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      {/* Overage column — hours + status Pill */}
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {row.overageHours != null && row.overageStatus !== 'none' ? (
                          <span className="flex items-center gap-2">
                            <span>{row.overageHours}h</span>
                            {overageColors && (
                              <Pill
                                bg={overageColors.bg}
                                fg={overageColors.fg}
                                dot={overageColors.dot}
                              >
                                {STATUS_LABELS[row.overageStatus as 'needs_approval' | 'ready']}
                              </Pill>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      {/* External costs column */}
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {row.externalCostsBilled > 0 ? (
                          <ReportingMoney value={row.externalCostsBilled} month={period} />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      {/* MKS-207 — open-timesheet signal. Names in the title
                          attribute so a long list does not break the row. */}
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {openTimesheets.length > 0 ? (
                          <span title={openTimesheets.map((p) => p.userName).join(', ')}>
                            {openTimesheets.length}{' '}
                            {openTimesheets.length === 1 ? 'person' : 'people'}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      {/* Status column — Pill with semantic inline colors */}
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-start gap-1">
                          <Pill bg={statusColors.bg} fg={statusColors.fg} dot={statusColors.dot}>
                            {twoStatusRow && row.status === 'ready'
                              ? 'Ready to invoice'
                              : STATUS_LABELS[row.status]}
                          </Pill>
                          {/* MKS-284 — why the row is red, one line per
                              reason, with up to three names then "+N".

                              MKS-280 — a green row can carry a reason too: a
                              month that is invoiceable from a carried-over
                              leftover usually has nothing logged in it yet,
                              and the queue says both. Red would contradict
                              the pill right above it, so a note on a ready
                              row is amber — worth reading, not in the way. */}
                          {twoStatusRow &&
                            reasons.map((reason) => (
                              <span
                                key={reason.code}
                                className={`text-[11px] ${
                                  row.status === 'ready' ? 'text-amber-700' : 'text-red-700'
                                }`}
                              >
                                <span className="font-semibold">{REASON_LABELS[reason.code]}</span>
                                {reason.users.length > 0 && `: ${formatReasonNames(reason.users)}`}
                              </span>
                            ))}
                          {/* MKS-207 — name the people whose hours have no
                              rate, so the fix is obvious from the queue. The
                              `no_rate` reason above says the same thing once
                              MKS-284 is on. */}
                          {!twoStatusRow && blockedUsers.length > 0 && (
                            <span className="text-[11px] text-red-700">
                              {blockedUsers.map((p) => p.userName).join(', ')}
                            </span>
                          )}
                          {contractValidity !== 'valid' && contractValidity !== 'unset' && (
                            <span className="text-[11px] text-amber-700">
                              {CONTRACT_LABELS[contractValidity]}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
